import React, { useEffect, createElement } from 'react';
import { ElegirPack } from './pages/ElegirPack';
const SITE_URL = 'https://bibliotecaprofesional.com/';
const SITE_TITLE = 'Biblioteca Profesional — MegaPacks digitales para profesionales y estudiantes';
const SITE_DESCRIPTION = 'Recursos digitales listos para usar: planes, sesiones, guías y actividades para Nutrición, Kinesiología, Estimulación Temprana, Psicología, Medicina y más.';
const SITE_KEYWORDS = 'biblioteca profesional, megapack, recursos digitales, nutrición, kinesiología, estimulación temprana, psicología, medicina, material clínico, planes de alimentación, guías de estudio';
const LCP_IMAGE = 'https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto,dpr_auto,w_500/v1779676327/nutri-Photoroom_lqu1oy.png';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap';
function upsertMeta(selector: string, create: () => HTMLElement, attrs: Record<string, string>) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}
function injectLink(attrs: Record<string, string>) {
  if (typeof document === 'undefined') return;
  const id = `mp-${attrs['data-mp']}`;
  const existing = document.head.querySelector(`link[data-mp="${attrs['data-mp']}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.id = id;
  Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
  document.head.appendChild(link);
}
function injectJsonLd(id: string, data: unknown) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}
export function App() {
  useEffect(() => {
    // -------- SEO --------
    document.documentElement.lang = 'es';
    document.title = SITE_TITLE;
    upsertMeta('meta[name="description"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      return m;
    }, {
      content: SITE_DESCRIPTION
    });
    upsertMeta('meta[name="keywords"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'keywords');
      return m;
    }, {
      content: SITE_KEYWORDS
    });
    upsertMeta('meta[name="robots"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      return m;
    }, {
      content: 'index, follow, max-image-preview:large'
    });
    upsertMeta('meta[name="viewport"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'viewport');
      return m;
    }, {
      content: 'width=device-width, initial-scale=1, viewport-fit=cover'
    });
    upsertMeta('meta[name="theme-color"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'theme-color');
      return m;
    }, {
      content: '#F6F5F2'
    });
    // Open Graph
    const og: Record<string, string> = {
      'og:title': SITE_TITLE,
      'og:description': SITE_DESCRIPTION,
      'og:type': 'website',
      'og:url': SITE_URL,
      'og:image': LCP_IMAGE,
      'og:locale': 'es_AR',
      'og:site_name': 'Biblioteca Profesional'
    };
    Object.entries(og).forEach(([property, content]) => {
      upsertMeta(`meta[property="${property}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute('property', property);
        return m;
      }, {
        content
      });
    });
    // Twitter Card
    const twitter: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': SITE_TITLE,
      'twitter:description': SITE_DESCRIPTION,
      'twitter:image': LCP_IMAGE
    };
    Object.entries(twitter).forEach(([name, content]) => {
      upsertMeta(`meta[name="${name}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute('name', name);
        return m;
      }, {
        content
      });
    });
    // Canonical
    upsertMeta('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    }, {
      href: SITE_URL
    });
    // Structured data — ItemList of products
    injectJsonLd('mp-jsonld-itemlist', {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: SITE_TITLE,
      description: SITE_DESCRIPTION,
      url: SITE_URL,
      numberOfItems: 7,
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'MegaPack Nutrición',
        url: 'https://nutripro.bibliotecaprofesional.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'MegaPack Kinesiología',
        url: 'https://kinesiologia.bibliotecaprofesional.com/'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: 'MegaPack Estimulación Temprana',
        url: 'https://estimulacion.bibliotecaprofesional.com/'
      }, {
        '@type': 'ListItem',
        position: 4,
        name: 'MegaPack Carrera Nutrición',
        url: 'https://carreranutri.bibliotecaprofesional.com/'
      }, {
        '@type': 'ListItem',
        position: 5,
        name: 'MegaPack Carrera Kinesiología',
        url: 'https://kinesiologia.bibliotecaprofesional.com/'
      }, {
        '@type': 'ListItem',
        position: 6,
        name: 'MegaPack Carrera Psicología',
        url: 'https://carrerapsico.bibliotecaprofesional.com/'
      }, {
        '@type': 'ListItem',
        position: 7,
        name: 'MegaPack Carrera Medicina',
        url: 'https://medicina.bibliotecaprofesional.com/'
      }]
    });
    injectJsonLd('mp-jsonld-organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Biblioteca Profesional',
      url: SITE_URL,
      logo: LCP_IMAGE,
      description: SITE_DESCRIPTION
    });
    // -------- Performance --------
    // Preconnect: parallel DNS+TLS to third-party origins
    injectLink({
      'data-mp': 'pc-cloudinary',
      rel: 'preconnect',
      href: 'https://res.cloudinary.com',
      crossorigin: ''
    });
    injectLink({
      'data-mp': 'pc-fonts-gs',
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com'
    });
    injectLink({
      'data-mp': 'pc-fonts-static',
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossorigin: ''
    });
    // Preload LCP image so the browser starts fetching before <img> commits
    injectLink({
      'data-mp': 'preload-lcp',
      rel: 'preload',
      as: 'image',
      href: LCP_IMAGE,
      fetchpriority: 'high'
    });
    // Async-load Google Fonts (non-render-blocking).
    // Uses the rel=preload + onload swap trick to avoid blocking first paint.
    if (!document.head.querySelector('link[data-mp="font-inter"]')) {
      const fontPreload = document.createElement('link');
      fontPreload.setAttribute('data-mp', 'font-inter');
      fontPreload.rel = 'preload';
      fontPreload.as = 'style';
      fontPreload.href = FONT_HREF;
      fontPreload.onload = function () {
        ;
        (this as HTMLLinkElement).onload = null;
        (this as HTMLLinkElement).rel = 'stylesheet';
      };
      document.head.appendChild(fontPreload);
      // <noscript> fallback for users with JS disabled (also helps SEO crawlers)
      const noscript = document.createElement('noscript');
      const fallback = document.createElement('link');
      fallback.rel = 'stylesheet';
      fallback.href = FONT_HREF;
      noscript.appendChild(fallback);
      document.head.appendChild(noscript);
    }
  }, []);
  return <ElegirPack data-id="element-0" />;
}